import { createCSSManager } from "@repo/mfe-core/host";
import { getMfeByName, loadRemote } from "@repo/mfe-registry";
import type { MfeModule } from "@repo/mfe-registry";
import { Box, Loader } from "@saviynt/design-system";
import { Suspense, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

interface MfeLoaderProps {
  name: string;
  fallback?: React.ReactNode;
  props?: Record<string, unknown>;
  hostManagesLoading?: boolean;
}

/**
 * Module-level singleton CSS Manager instance.
 * Shared across all MfeLoader instances so inject/remove are coordinated.
 */
const cssManager = createCSSManager({
  onError: (mfeName, error) => {
    console.warn(`[MfeLoader] CSS issue for "${mfeName}":`, error.message);
  },
});

/**
 * Default CSS asset filename relative to the MFE's entry URL base path.
 * Guaranteed by createMfViteConfig: cssCodeSplit: false + assetFileNames: "assets/[name][extname]"
 */
const MFE_CSS_ASSET_PATH = "assets/style.css";

/**
 * Derives the CSS URL from the MFE's entry URL.
 * Strips the filename (e.g. remoteEntry.js) and appends the default CSS asset path.
 *
 * Examples:
 *   "/mfe/integrations/module-federation/remoteEntry.js"
 *     → "/mfe/integrations/module-federation/assets/style.css"
 *
 *   "http://localhost:3002/remoteEntry.js"
 *     → "http://localhost:3002/assets/style.css"
 */
function deriveCssUrl(entryUrl: string): string {
  const lastSlashIndex = entryUrl.lastIndexOf("/");
  const basePath =
    lastSlashIndex >= 0 ? entryUrl.substring(0, lastSlashIndex + 1) : "";
  return `${basePath}${MFE_CSS_ASSET_PATH}`;
}

const DefaultFallback = () => (
  <Box className="w-full h-full flex justify-center items-center">
    <Loader kind="loop" format="main" color="brand" />
  </Box>
);

export function MfeLoader({
  name,
  fallback = <DefaultFallback />,
  props = {},
  hostManagesLoading = false,
}: MfeLoaderProps) {
  const [Component, setComponent] = useState<React.ComponentType | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [key, setKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setComponent(null);
    setError(null);

    // 1. Look up MFE in registry
    const mfe = getMfeByName(name);
    if (!mfe) {
      setError(new Error(`MFE "${name}" not found in registry`));
      return;
    }

    // 2. Derive CSS URL from the entry URL (or use explicit cssAssetPath if defined)
    const entryUrl =
      import.meta.env.PROD && mfe.moduleFederation.prodUrl
        ? mfe.moduleFederation.prodUrl
        : mfe.moduleFederation.devUrl;
    const cssUrl = mfe.moduleFederation.cssAssetPath || deriveCssUrl(entryUrl);

    // 3. Inject CSS, then load remote module
    async function loadMfe() {
      try {
        // Inject CSS and await (resolves on load, error, or timeout — graceful degradation)
        await cssManager.inject(name, cssUrl);

        if (cancelled) return;

        // Load the remote module via registry's loadRemote (backed by RuntimeLoader)
        const module: MfeModule = await loadRemote(name);

        if (cancelled) return;

        // Extract the component from the module
        const Comp = module.default || module.App;
        if (Comp) {
          setComponent(() => Comp as React.ComponentType);
          if (import.meta.env.DEV) {
            setKey((k) => k + 1);
          }
        } else {
          setError(new Error("No component exported from remote"));
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err : new Error("Failed to load MFE"),
          );
        }
      }
    }

    loadMfe();

    // 4. Cleanup: remove CSS on unmount
    return () => {
      cancelled = true;
      cssManager.remove(name);
    };
  }, [name]);

  if (error) {
    return (
      <div className="p-4 text-[var(--color-foreground-critical-bold)]">
        <FormattedMessage
          id="KkbFrB"
          defaultMessage="Error loading remote: {message}"
          description="Error message when MFE fails to load"
          values={{ message: error.message }}
        />
      </div>
    );
  }

  if (!Component) {
    if (hostManagesLoading) {
      return null;
    }
    return <>{fallback}</>;
  }

  return (
    <Suspense fallback={null}>
      <Component key={key} {...props} />
    </Suspense>
  );
}

export function useDynamicRemote(name: string) {
  const [module, setModule] = useState<MfeModule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    loadRemote(name)
      .then((mod) => {
        setModule(mod);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [name]);

  return { module, loading, error };
}
